import { useLayoutEffect, useRef, useState, type Ref } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose, PanelLeft, X, ChevronDown, ChevronRight } from 'lucide-react';
import { NAV_SECTIONS } from '../../config/navigation';
import { Logo } from './Logo';

interface SidebarProps {
  /** Desktop: icon-only when collapsed. */
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** Mobile: drawer open state. */
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

const SECTIONS_EXPANDED_KEY = 'navSectionsExpanded';

const isActive = (pathname: string, to: string) =>
  to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

/** The section that owns the currently active route (used to auto-expand it). */
const activeSectionLabel = (pathname: string): string | undefined =>
  NAV_SECTIONS.find((section) => section.items.some((item) => isActive(pathname, item.to)))?.label;

/**
 * Initial expanded-state for accordion sections.
 * - Restores the saved preference when present.
 * - Otherwise: on laptop screens (< xl) only the active route's section is open
 *   so the nav is compact; on wide screens every section starts expanded.
 */
const initialExpandedSections = (pathname: string): Record<string, boolean> => {
  try {
    const stored = localStorage.getItem(SECTIONS_EXPANDED_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    /* ignore malformed storage */
  }
  const isLaptop = typeof window !== 'undefined' && window.innerWidth < 1280;
  const active = activeSectionLabel(pathname);
  return Object.fromEntries(
    NAV_SECTIONS.map((section) => [section.label, isLaptop ? section.label === active : true])
  );
};

/**
 * Grouped, collapsible navigation sidebar.
 *
 * - Desktop (md+): a persistent column that collapses to an icon rail.
 * - Mobile: an off-canvas drawer toggled from the top bar.
 * - Each section is an accordion group you can expand/collapse (compact on laptops).
 * Sections and links come from NAV_SECTIONS (config/navigation.ts).
 */
export const Sidebar = ({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    initialExpandedSections(location.pathname)
  );
  // The scrollable desktop nav container — measured to auto-collapse groups.
  const navRef = useRef<HTMLElement | null>(null);

  const go = (to: string) => {
    navigate(to);
    onCloseMobile();
  };

  // Auto-collapse: if the desktop nav would overflow (e.g. when the user zooms
  // in or the viewport shrinks), collapse the lowest-priority open section that
  // doesn't own the current route, one at a time, until everything fits without
  // a scrollbar. Runs after layout and whenever the nav is resized. This does
  // NOT persist to localStorage, so the user's saved preferences are untouched.
  useLayoutEffect(() => {
    if (collapsed) return; // icon rail has no accordion
    const el = navRef.current;
    if (!el) return;

    const TOLERANCE = 2; // px, avoid churn from sub-pixel rounding
    const fit = () => {
      const node = navRef.current;
      if (!node) return;
      if (node.scrollHeight - node.clientHeight <= TOLERANCE) return; // fits
      const active = activeSectionLabel(location.pathname);
      setExpandedSections((prev) => {
        // Collapse from the bottom up, skipping the active section.
        for (let i = NAV_SECTIONS.length - 1; i >= 0; i--) {
          const label = NAV_SECTIONS[i].label;
          if (label === active) continue;
          if (prev[label] !== false) {
            return { ...prev, [label]: false };
          }
        }
        return prev; // nothing left to collapse — accept the scroll
      });
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed, location.pathname, expandedSections]);

  const toggleSection = (label: string) => {
    setExpandedSections((prev) => {
      const next = { ...prev, [label]: !prev[label] };
      try {
        localStorage.setItem(SECTIONS_EXPANDED_KEY, JSON.stringify(next));
      } catch {
        /* ignore storage failures */
      }
      return next;
    });
  };

  const renderNav = (isCollapsed: boolean, ref?: Ref<HTMLElement>) => (
    <nav ref={ref} className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
      {NAV_SECTIONS.map((section) => {
        // In the icon rail there are no labels, so the accordion doesn't apply —
        // always show every item's icon.
        const isOpen = isCollapsed || expandedSections[section.label] !== false;
        return (
          <div key={section.label}>
            {!isCollapsed && (
              <button
                type="button"
                onClick={() => toggleSection(section.label)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <span>{section.label}</span>
                {isOpen ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            {isCollapsed && <div className="mx-2 mb-1 border-t border-gray-200 dark:border-gray-700" />}
            {isOpen && (
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const active = isActive(location.pathname, item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={(e) => {
                        e.preventDefault();
                        go(item.to);
                      }}
                      title={isCollapsed ? item.label : undefined}
                      aria-current={active ? 'page' : undefined}
                      className={`group flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors ${
                        isCollapsed ? 'justify-center' : ''
                      } ${
                        active
                          ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <item.icon
                        className={`h-[18px] w-[18px] flex-shrink-0 ${
                          active ? '' : 'opacity-80 group-hover:opacity-100'
                        }`}
                        strokeWidth={2.25}
                      />
                      {!isCollapsed && <span className="truncate">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex flex-col flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 transition-[width] duration-200 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        <div className={`flex items-center h-16 px-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && <Logo />}
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
        </div>
        {renderNav(collapsed, navRef)}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={onCloseMobile} aria-hidden="true" />
          <aside className="relative flex flex-col w-64 max-w-[80%] bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex items-center justify-between h-16 px-3">
              <Logo />
              <button
                onClick={onCloseMobile}
                className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {renderNav(false)}
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
